import * as React from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CheckboxProps {
  checked: boolean | 'indeterminate';
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
}

/**
 * Minimal brand-themed tri-state checkbox. We deliberately don't pull in
 * @radix-ui/react-checkbox here — the surface area used by the DataTable
 * is small (select-row, select-all-with-indeterminate) and a styled
 * native input keeps the bundle tighter and the focus ring identical to
 * the rest of the shadcn primitives.
 */
export const Checkbox = React.forwardRef<HTMLButtonElement, CheckboxProps>(
  ({ checked, onCheckedChange, disabled, ariaLabel, className }, ref) => {
    const isIndeterminate = checked === 'indeterminate';
    const isChecked = checked === true;
    return (
      <button
        ref={ref}
        type="button"
        role="checkbox"
        aria-checked={isIndeterminate ? 'mixed' : isChecked}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          onCheckedChange?.(!isChecked);
        }}
        className={cn(
          'flex h-4 w-4 shrink-0 items-center justify-center rounded border border-border bg-card transition',
          'hover:border-info/60 focus:outline-none focus:ring-2 focus:ring-info/40',
          (isChecked || isIndeterminate) && 'border-info bg-info text-info-foreground',
          disabled && 'cursor-not-allowed opacity-50',
          className,
        )}
      >
        {isIndeterminate ? (
          <Minus className="h-3 w-3" />
        ) : isChecked ? (
          <Check className="h-3 w-3" />
        ) : null}
      </button>
    );
  },
);
Checkbox.displayName = 'Checkbox';
